import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './Envase.css';

const TIPOS_ENTREGA = [
    { codigo: 'INM',  nombre: 'Inmediata' },
    { codigo: 'PROG', nombre: 'Programada' },
    { codigo: 'PARC', nombre: 'Parcial' },
    { codigo: 'RETR', nombre: 'Retiro' },
    { codigo: 'ENTR', nombre: 'Entrega' }
];

const CONDICIONES = [
    { codigo: 'CTD', nombre: 'Contado' },
    { codigo: 'CRD', nombre: 'Credito' },
    { codigo: 'MIX', nombre: 'Mixto' }
];

const formatearFecha = (f) => {
    if (!f) return '';
    const [y, m, d] = String(f).split('-');
    return `${d}/${m}/${y}`;
};

const obtenerFechaHoy = () => new Date().toISOString().split('T')[0];

const formatearNumero = (v, dec = 2) => {
    if (v == null || v === '') return '0,00';
    return Number(v).toLocaleString('es-PY', {
        minimumFractionDigits: dec, maximumFractionDigits: dec
    });
};

const calcularVencimiento = (fechaStr, dias) => {
    if (!fechaStr || dias == null) return '';
    const fecha = new Date(fechaStr + 'T00:00:00');
    fecha.setDate(fecha.getDate() + Number(dias));
    return fecha.toISOString().split('T')[0];
};

const formularioInicial = () => ({
    idProveedor: '',
    nomProveedor: '',
    rucProveedor: '',
    fecha: obtenerFechaHoy(),
    parafecha: '',
    idFpago: '',
    condcompra: 'CTD',
    tipoentrega: 'INM',
    comprade: '',
    ccosto: '',
    monExenta: 0,
    monGravada5: 0,
    monGravada10: 0,
    monIva5: 0,
    monIva10: 0,
    monTotal: 0,
    observacion: '',
    detalles: []
});

function SOrdencompra({ usuario }) {
    const [ordenes, setOrdenes] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState(10);

    const [modalAbierto, setModalAbierto] = useState(false);
    const [modalVerAbierto, setModalVerAbierto] = useState(false);
    const [ordenVer, setOrdenVer] = useState(null);
    const [formulario, setFormulario] = useState(formularioInicial());

    const [busquedaProv, setBusquedaProv] = useState('');
    const [provsEncontrados, setProvsEncontrados] = useState([]);

    const [busquedaProducto, setBusquedaProducto] = useState('');
    const [productosEncontrados, setProductosEncontrados] = useState([]);
    const [detalleTemp, setDetalleTemp] = useState({
        idProducto: '', descripcion: '', cantidad: 1, precio: 0, gravada: 10
    });

    const [formasPago, setFormasPago] = useState([]);

    useEffect(() => { cargar(); cargarCatalogos(); }, []);

    const cargar = async () => {
        try {
            const r = await axios.get('/api/ordencompra');
            setOrdenes(r.data);
        } catch (e) {
            console.error('Error al cargar ordenes:', e);
            alert('Error al cargar ordenes de compra');
        }
    };

    const cargarCatalogos = async () => {
        try {
            const r = await axios.get('/api/fpago');
            setFormasPago(r.data);
        } catch (e) { console.error(e); }
    };

    // Filtrado
    const filtradas = ordenes.filter(o => {
        if (fechaDesde && (!o.fecha || o.fecha < fechaDesde)) return false;
        if (fechaHasta && (!o.fecha || o.fecha > fechaHasta)) return false;
        if (busqueda) {
            const q = busqueda.toLowerCase();
            const matches =
                o.ordencompra?.toString().includes(busqueda) ||
                (o.nomProveedor || '').toLowerCase().includes(q) ||
                (o.observacion || '').toLowerCase().includes(q);
            if (!matches) return false;
        }
        return true;
    });

    const totalPaginas = Math.max(1, Math.ceil(filtradas.length / porPagina));
    const inicio = (pagina - 1) * porPagina;
    const paginadas = filtradas.slice(inicio, inicio + porPagina);

    // ===== Alta =====
    const abrirModalNuevo = () => {
        setFormulario(formularioInicial());
        setBusquedaProv('');
        setProvsEncontrados([]);
        setBusquedaProducto('');
        setProductosEncontrados([]);
        setDetalleTemp({ idProducto: '', descripcion: '', cantidad: 1, precio: 0, gravada: 10 });
        setModalAbierto(true);
    };

    const cerrarModal = () => setModalAbierto(false);

    const buscarProvAPI = async (texto) => {
        setBusquedaProv(texto);
        if (texto.length < 2) { setProvsEncontrados([]); return; }
        try {
            const r = await axios.get('/api/ordencompra/buscar-proveedor', { params: { busqueda: texto } });
            setProvsEncontrados(r.data);
        } catch (e) { console.error(e); }
    };

    const seleccionarProveedor = (p) => {
        setFormulario(f => ({
            ...f,
            idProveedor: p.id_proveedor,
            nomProveedor: p.nom_proveedor,
            rucProveedor: p.ruc_prov
        }));
        setBusquedaProv('');
        setProvsEncontrados([]);
    };

    // Busqueda de productos: 250 ms tras la ultima tecla; solo se muestra la respuesta de la ultima busqueda
    const busquedaProductoRef = useRef({ timer: null, secuencia: 0 });
    const buscarProductosAPI = (texto) => {
        setBusquedaProducto(texto);
        const ref = busquedaProductoRef.current;
        clearTimeout(ref.timer);
        if (texto.trim().length < 2) { ref.secuencia++; setProductosEncontrados([]); return; }
        const seq = ++ref.secuencia;
        ref.timer = setTimeout(async () => {
            try {
                const r = await axios.get('/api/ordencompra/buscar-producto', { params: { busqueda: texto } });
                if (seq === ref.secuencia) setProductosEncontrados(r.data);
            } catch (e) { console.error(e); }
        }, 250);
    };

    const seleccionarProducto = (p) => {
        setDetalleTemp({
            idProducto: p.id_producto,
            descripcion: p.descripcion,
            cantidad: 1,
            precio: Number(p.costoprom) || 0,
            gravada: Number(p.gravada) || 10
        });
        setBusquedaProducto(`${p.id_producto} - ${p.descripcion}`);
        setProductosEncontrados([]);
    };

    const seleccionarFormaPago = (idFpago) => {
        const fp = formasPago.find(x => x.idFpago === idFpago);
        setFormulario(f => ({
            ...f,
            idFpago,
            parafecha: fp && fp.vdias1 != null ? calcularVencimiento(f.fecha, fp.vdias1) : f.parafecha
        }));
    };

    const cambiarFecha = (nuevaFecha) => {
        setFormulario(f => {
            const fp = formasPago.find(x => x.idFpago === f.idFpago);
            return {
                ...f,
                fecha: nuevaFecha,
                parafecha: fp && fp.vdias1 != null ? calcularVencimiento(nuevaFecha, fp.vdias1) : f.parafecha
            };
        });
    };

    // Calcula distribucion IVA (5%, 10%, exenta) segun el porcentaje del producto
    const calcularLinea = (cantidad, precio, gravada) => {
        const monto = Number(cantidad) * Number(precio);
        let exenta = 0, grav5 = 0, grav10 = 0, iva5 = 0, iva10 = 0;
        if (gravada === 0 || gravada === '0') {
            exenta = monto;
        } else if (gravada === 5 || gravada === '5') {
            grav5 = Math.round(monto / 1.05);
            iva5  = monto - grav5;
        } else if (gravada === 10 || gravada === '10') {
            grav10 = Math.round(monto / 1.10);
            iva10  = monto - grav10;
        }
        return { exenta, grav5, grav10, iva5, iva10, total: monto };
    };

    const agregarDetalle = () => {
        if (!detalleTemp.idProducto) { alert('Seleccione un producto'); return; }
        if (detalleTemp.cantidad <= 0) { alert('Cantidad debe ser mayor a 0'); return; }
        if (detalleTemp.precio < 0) { alert('El precio no puede ser negativo'); return; }
        if (formulario.detalles.some(d => d.idProducto === detalleTemp.idProducto)) {
            alert('El producto ya esta en el detalle'); return;
        }

        const calc = calcularLinea(detalleTemp.cantidad, detalleTemp.precio, detalleTemp.gravada);
        setFormulario(f => {
            const nuevoDetalle = {
                idProducto: detalleTemp.idProducto,
                descripcion: detalleTemp.descripcion?.substring(0, 50),
                cantidad: Number(detalleTemp.cantidad),
                precio: Number(detalleTemp.precio),
                gravada: Number(detalleTemp.gravada),
                monExenta: calc.exenta,
                monGravada5: calc.grav5,
                monGravada10: calc.grav10,
                monIva5: calc.iva5,
                monIva10: calc.iva10,
                monTotal: calc.total
            };
            const detalles = [...f.detalles, nuevoDetalle];
            const totales = detalles.reduce((acc, d) => ({
                exenta:  acc.exenta  + (Number(d.monExenta)   || 0),
                grav5:   acc.grav5   + (Number(d.monGravada5) || 0),
                grav10:  acc.grav10  + (Number(d.monGravada10)|| 0),
                iva5:    acc.iva5    + (Number(d.monIva5)     || 0),
                iva10:   acc.iva10   + (Number(d.monIva10)    || 0),
                total:   acc.total   + (Number(d.monTotal)    || 0)
            }), { exenta: 0, grav5: 0, grav10: 0, iva5: 0, iva10: 0, total: 0 });
            return {
                ...f, detalles,
                monExenta: totales.exenta,
                monGravada5: totales.grav5,
                monGravada10: totales.grav10,
                monIva5: totales.iva5,
                monIva10: totales.iva10,
                monTotal: totales.total
            };
        });
        setDetalleTemp({ idProducto: '', descripcion: '', cantidad: 1, precio: 0, gravada: 10 });
        setBusquedaProducto('');
    };

    const quitarDetalle = (idProducto) => {
        setFormulario(f => {
            const detalles = f.detalles.filter(d => d.idProducto !== idProducto);
            const totales = detalles.reduce((acc, d) => ({
                exenta:  acc.exenta  + (Number(d.monExenta)   || 0),
                grav5:   acc.grav5   + (Number(d.monGravada5) || 0),
                grav10:  acc.grav10  + (Number(d.monGravada10)|| 0),
                iva5:    acc.iva5    + (Number(d.monIva5)     || 0),
                iva10:   acc.iva10   + (Number(d.monIva10)    || 0),
                total:   acc.total   + (Number(d.monTotal)    || 0)
            }), { exenta: 0, grav5: 0, grav10: 0, iva5: 0, iva10: 0, total: 0 });
            return {
                ...f, detalles,
                monExenta: totales.exenta,
                monGravada5: totales.grav5,
                monGravada10: totales.grav10,
                monIva5: totales.iva5,
                monIva10: totales.iva10,
                monTotal: totales.total
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formulario.idProveedor) { alert('Seleccione el proveedor'); return; }
        if (formulario.detalles.length === 0) { alert('Agregue al menos un producto'); return; }

        const payload = {
            idProveedor: Number(formulario.idProveedor),
            fecha: formulario.fecha,
            parafecha: formulario.parafecha || null,
            idFpago: formulario.idFpago || null,
            condcompra: formulario.condcompra || null,
            tipoentrega: formulario.tipoentrega || null,
            comprade: formulario.comprade || null,
            ccosto: formulario.ccosto ? Number(formulario.ccosto) : null,
            monExenta: Number(formulario.monExenta) || 0,
            monGravada5: Number(formulario.monGravada5) || 0,
            monGravada10: Number(formulario.monGravada10) || 0,
            monIva5: Number(formulario.monIva5) || 0,
            monIva10: Number(formulario.monIva10) || 0,
            monTotal: Number(formulario.monTotal) || 0,
            observacion: formulario.observacion || null,
            detalles: formulario.detalles.map(d => ({
                idProducto: d.idProducto,
                descripcion: d.descripcion,
                cantidad: d.cantidad,
                precio: d.precio,
                monExenta: d.monExenta,
                monGravada5: d.monGravada5,
                monGravada10: d.monGravada10,
                monIva5: d.monIva5,
                monIva10: d.monIva10,
                monTotal: d.monTotal
            }))
        };

        try {
            await axios.post('/api/ordencompra', payload);
            alert('Orden de compra registrada');
            cerrarModal();
            cargar();
        } catch (error) {
            const msg = error.response?.data?.message || 'Error al guardar';
            alert(msg);
        }
    };

    const verOrden = async (o) => {
        try {
            const r = await axios.get(`/api/ordencompra/${o.ordencompra}`);
            setOrdenVer(r.data);
        } catch (e) {
            console.error(e);
            setOrdenVer(o);
        }
        setModalVerAbierto(true);
    };

    const eliminar = async (o) => {
        if (!window.confirm(`Eliminar la orden de compra #${o.ordencompra}?\n\nEsta accion es irreversible.`)) return;
        try {
            await axios.delete(`/api/ordencompra/${o.ordencompra}`);
            alert('Orden eliminada');
            cargar();
        } catch (error) {
            const msg = error.response?.data?.message || 'Error al eliminar';
            alert(msg);
        }
    };

    const exportarExcel = () => {
        if (filtradas.length === 0) { alert('No hay datos para exportar'); return; }
        const datos = filtradas.map(o => ({
            'N° Orden': o.ordencompra,
            'Proveedor ID': o.idProveedor,
            'Proveedor': o.nomProveedor || '',
            'Fecha': formatearFecha(o.fecha),
            'Para Fecha': formatearFecha(o.parafecha),
            'Forma Pago': o.idFpago,
            'Condicion': o.condcompra,
            'Tipo Entrega': o.tipoentrega,
            'Mon. Exenta': Number(o.monExenta || 0),
            'Mon. Gravada 5%': Number(o.monGravada5 || 0),
            'Mon. Gravada 10%': Number(o.monGravada10 || 0),
            'Mon. IVA 5%': Number(o.monIva5 || 0),
            'Mon. IVA 10%': Number(o.monIva10 || 0),
            'Mon. Total': Number(o.monTotal || 0),
            'Observacion': o.observacion
        }));
        const ws = XLSX.utils.json_to_sheet(datos);
        ws['!cols'] = Object.keys(datos[0]).map(k => ({ wch: Math.max(k.length, 14) }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Ordenes');
        XLSX.writeFile(wb, `ordenes_compra_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <div className="envase-container">
            <div className="header-seccion">
                <h2>Ordenes de Compra</h2>
                <div className="acciones-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #ddd', borderRadius: '5px', padding: '4px 8px', backgroundColor: '#fff' }}>
                        <label style={{ fontSize: '12px', color: '#555' }}>📅 Desde</label>
                        <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
                            style={{ padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }} />
                        <label style={{ fontSize: '12px', color: '#555' }}>Hasta</label>
                        <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
                            style={{ padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }} />
                        {(fechaDesde || fechaHasta) && (
                            <button type="button" onClick={() => { setFechaDesde(''); setFechaHasta(''); }}
                                style={{ padding: '2px 6px', border: 'none', backgroundColor: 'transparent', color: '#d97706', cursor: 'pointer' }}>✕</button>
                        )}
                    </div>
                    <input type="text" placeholder="Buscar..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="input-busqueda" />
                    <button onClick={abrirModalNuevo} className="btn btn-nuevo">➕ Nueva Orden</button>
                    <button onClick={exportarExcel} className="btn btn-exportar">📊 Exportar Excel</button>
                </div>
            </div>

            <div className="tabla-container">
                <table className="tabla-datos">
                    <thead>
                        <tr>
                            <th>N° Orden</th>
                            <th>Proveedor</th>
                            <th>Fecha</th>
                            <th>Para Fecha</th>
                            <th>Forma Pago</th>
                            <th>Entrega</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginadas.length === 0 ? (
                            <tr><td colSpan="9" className="sin-datos">No hay ordenes de compra</td></tr>
                        ) : paginadas.map(o => (
                            <tr key={o.ordencompra} style={o.estado === 1 ? { opacity: 0.6 } : {}}>
                                <td>{o.ordencompra}</td>
                                <td>{o.idProveedor} - {o.nomProveedor || '-'}</td>
                                <td>{formatearFecha(o.fecha)}</td>
                                <td>{formatearFecha(o.parafecha)}</td>
                                <td>{o.idFpago || '-'}</td>
                                <td>{o.tipoentrega || '-'}</td>
                                <td style={{ textAlign: 'right' }}>{formatearNumero(o.monTotal)}</td>
                                <td>
                                    <span style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        borderRadius: '10px',
                                        backgroundColor: o.estado === 1 ? '#f0f0f0' : '#E4F2FC',
                                        color: o.estado === 1 ? '#666' : '#004F9F',
                                        fontSize: '11px',
                                        fontWeight: '600'
                                    }}>
                                        {o.estado === 1 ? '🔒 Cerrada' : '🟢 Abierta'}
                                    </span>
                                </td>
                                <td>
                                    <button onClick={() => verOrden(o)} className="btn-accion" title="Ver detalle">👁</button>
                                    <button onClick={() => eliminar(o)} className="btn-accion" title="Eliminar"
                                        disabled={o.estado === 1}
                                        style={{ color: o.estado === 1 ? '#ccc' : '#c62828' }}>🗑️</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="paginacion-container">
                <div className="paginacion-info">
                    <span>Mostrando {filtradas.length > 0 ? inicio + 1 : 0} a {Math.min(inicio + porPagina, filtradas.length)} de {filtradas.length}</span>
                    <select value={porPagina} onChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(1); }} className="select-registros">
                        <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                    </select>
                </div>
                <div className="paginacion-controles">
                    <button onClick={() => setPagina(1)} disabled={pagina === 1} className="btn-paginacion">|&lt;</button>
                    <button onClick={() => setPagina(p => p - 1)} disabled={pagina === 1} className="btn-paginacion">&lt;</button>
                    <span className="paginacion-numero">{pagina} / {totalPaginas}</span>
                    <button onClick={() => setPagina(p => p + 1)} disabled={pagina >= totalPaginas} className="btn-paginacion">&gt;</button>
                    <button onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas} className="btn-paginacion">&gt;|</button>
                </div>
            </div>

            {/* Modal Nueva Orden */}
            {modalAbierto && (
                <div className="atm-modal-overlay" onClick={cerrarModal}>
                    <div className="atm-modal atm-modal-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="atm-modal-header">
                            <h3>Nueva Orden de Compra</h3>
                            <button onClick={cerrarModal} className="atm-modal-close">×</button>
                        </div>

                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                            <div className="atm-modal-body">

                                {/* Cabecera */}
                                <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa' }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: '#004F9F', borderBottom: '2px solid #004F9F', paddingBottom: '5px' }}>Cabecera</h4>

                                    <div className="form-group" style={{ position: 'relative', marginBottom: '10px' }}>
                                        <label>Proveedor *</label>
                                        {formulario.idProveedor ? (
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '8px', backgroundColor: '#E4F2FC', borderRadius: '5px' }}>
                                                <strong>{formulario.idProveedor}</strong> - {formulario.nomProveedor}
                                                <span style={{ fontSize: '12px', color: '#555' }}>({formulario.rucProveedor})</span>
                                                <button type="button"
                                                    onClick={() => setFormulario(f => ({ ...f, idProveedor: '', nomProveedor: '', rucProveedor: '' }))}
                                                    style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#d97706', cursor: 'pointer' }}>✕ cambiar</button>
                                            </div>
                                        ) : (
                                            <input type="text" placeholder="Buscar proveedor por nombre o RUC (min 2 chars)..."
                                                value={busquedaProv} onChange={(e) => buscarProvAPI(e.target.value)}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        )}
                                        {provsEncontrados.length > 0 && !formulario.idProveedor && (
                                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff',
                                                border: '1px solid #ccc', borderRadius: '5px', maxHeight: '250px', overflowY: 'auto',
                                                zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                                                {provsEncontrados.map(p => (
                                                    <div key={p.id_proveedor} onClick={() => seleccionarProveedor(p)}
                                                        style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '13px' }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E4F2FC'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                                                        <strong>{p.id_proveedor}</strong> - {p.nom_proveedor}
                                                        {p.ruc_prov && <span style={{ color: '#555', marginLeft: '8px' }}>({p.ruc_prov})</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px' }}>
                                        <div className="form-group">
                                            <label>Fecha *</label>
                                            <input type="date" value={formulario.fecha} required
                                                onChange={(e) => cambiarFecha(e.target.value)}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Forma de Pago</label>
                                            <select value={formulario.idFpago}
                                                onChange={(e) => seleccionarFormaPago(e.target.value)}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                <option value="">-- Seleccione --</option>
                                                {formasPago.map(fp => (
                                                    <option key={fp.idFpago} value={fp.idFpago}>
                                                        {fp.idFpago}{fp.vdias1 != null ? ` (${fp.vdias1} dias)` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Para Fecha (entrega)</label>
                                            <input type="date" value={formulario.parafecha}
                                                onChange={(e) => setFormulario(f => ({ ...f, parafecha: e.target.value }))}
                                                title="Calculado segun forma de pago; editable"
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Tipo Entrega</label>
                                            <select value={formulario.tipoentrega}
                                                onChange={(e) => setFormulario(f => ({ ...f, tipoentrega: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                {TIPOS_ENTREGA.map(t => <option key={t.codigo} value={t.codigo}>{t.codigo} - {t.nombre}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Condicion Compra</label>
                                            <select value={formulario.condcompra}
                                                onChange={(e) => setFormulario(f => ({ ...f, condcompra: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                {CONDICIONES.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo} - {c.nombre}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Compra de (destino)</label>
                                            <input type="text" value={formulario.comprade} maxLength={4}
                                                placeholder="Ej: 001"
                                                onChange={(e) => setFormulario(f => ({ ...f, comprade: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>C. Costo</label>
                                            <input type="number" value={formulario.ccosto}
                                                onChange={(e) => setFormulario(f => ({ ...f, ccosto: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        </div>
                                        <div className="form-group" style={{ gridColumn: 'span 4' }}>
                                            <label>Observacion</label>
                                            <textarea value={formulario.observacion} rows={2}
                                                onChange={(e) => setFormulario(f => ({ ...f, observacion: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px', resize: 'vertical' }} />
                                        </div>
                                    </div>
                                </div>

                                {/* Agregar producto */}
                                <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#EDF1FB' }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: '#004F9F' }}>Agregar producto al detalle</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
                                        <div className="form-group" style={{ position: 'relative' }}>
                                            <label>Producto (min 2 caracteres)</label>
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                <input type="text" placeholder="Buscar por descripcion o codigo..."
                                                    value={busquedaProducto}
                                                    onChange={(e) => buscarProductosAPI(e.target.value)}
                                                    readOnly={!!detalleTemp.idProducto}
                                                    style={{
                                                        flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '5px',
                                                        backgroundColor: detalleTemp.idProducto ? '#f0f0f0' : '#fff',
                                                        cursor: detalleTemp.idProducto ? 'not-allowed' : 'text'
                                                    }} />
                                                {detalleTemp.idProducto && (
                                                    <button type="button"
                                                        title="Limpiar seleccion"
                                                        onClick={() => {
                                                            setDetalleTemp({ idProducto: '', descripcion: '', cantidad: 1, precio: 0, gravada: 10 });
                                                            setBusquedaProducto('');
                                                        }}
                                                        style={{
                                                            padding: '4px 10px', border: '1px solid #d97706',
                                                            backgroundColor: '#fff7ed', color: '#d97706',
                                                            borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold'
                                                        }}>✕</button>
                                                )}
                                            </div>
                                            {productosEncontrados.length > 0 && (
                                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff',
                                                    border: '1px solid #ccc', borderRadius: '5px', maxHeight: '250px', overflowY: 'auto',
                                                    zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                                                    {productosEncontrados.map(p => (
                                                        <div key={p.id_producto} onClick={() => seleccionarProducto(p)}
                                                            style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '13px' }}
                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f9ff'}
                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                                                            <strong>{p.id_producto}</strong> - {p.descripcion}
                                                            <span style={{ marginLeft: '8px', color: '#555', fontSize: '12px' }}>
                                                                IVA {p.gravada}%
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="form-group">
                                            <label>Cantidad *</label>
                                            <input type="number" value={detalleTemp.cantidad} min="0" step="any"
                                                onChange={(e) => setDetalleTemp(d => ({ ...d, cantidad: Number(e.target.value) }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px', textAlign: 'right' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Precio Unit.</label>
                                            <input type="number" value={detalleTemp.precio} min="0" step="any"
                                                onChange={(e) => setDetalleTemp(d => ({ ...d, precio: Number(e.target.value) }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px', textAlign: 'right' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>IVA %</label>
                                            <select value={detalleTemp.gravada}
                                                onChange={(e) => setDetalleTemp(d => ({ ...d, gravada: Number(e.target.value) }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                <option value={0}>0% Exenta</option>
                                                <option value={5}>5%</option>
                                                <option value={10}>10%</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <button type="button" onClick={agregarDetalle}
                                                disabled={!detalleTemp.idProducto}
                                                className="btn btn-nuevo" style={{ marginTop: '5px' }}>+ Agregar</button>
                                        </div>
                                    </div>
                                </div>

                                {/* Detalle */}
                                {formulario.detalles.length > 0 && (
                                    <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa' }}>
                                        <h4 style={{ margin: '0 0 10px 0', color: '#004F9F' }}>Detalle ({formulario.detalles.length} productos)</h4>
                                        <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                            <thead>
                                                <tr>
                                                    <th>ID</th><th>Producto</th>
                                                    <th style={{ textAlign: 'right' }}>Cant.</th>
                                                    <th style={{ textAlign: 'right' }}>Precio</th>
                                                    <th>IVA</th>
                                                    <th style={{ textAlign: 'right' }}>Exenta</th>
                                                    <th style={{ textAlign: 'right' }}>Grav. 5%</th>
                                                    <th style={{ textAlign: 'right' }}>Grav. 10%</th>
                                                    <th style={{ textAlign: 'right' }}>IVA</th>
                                                    <th style={{ textAlign: 'right' }}>Total</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formulario.detalles.map(d => (
                                                    <tr key={d.idProducto}>
                                                        <td>{d.idProducto}</td>
                                                        <td>{d.descripcion}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(d.cantidad, 2)}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(d.precio, 2)}</td>
                                                        <td>{d.gravada}%</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(d.monExenta, 2)}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(d.monGravada5, 2)}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(d.monGravada10, 2)}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(Number(d.monIva5) + Number(d.monIva10), 2)}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(d.monTotal, 2)}</td>
                                                        <td><button type="button" onClick={() => quitarDetalle(d.idProducto)} className="btn-accion">🗑</button></td>
                                                    </tr>
                                                ))}
                                                <tr style={{ backgroundColor: '#E4F2FC', fontWeight: 'bold' }}>
                                                    <td colSpan="5" style={{ textAlign: 'right' }}>TOTALES</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(formulario.monExenta)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(formulario.monGravada5)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(formulario.monGravada10)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(Number(formulario.monIva5) + Number(formulario.monIva10))}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(formulario.monTotal)}</td>
                                                    <td></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className="atm-modal-footer">
                                <button type="button" onClick={cerrarModal} className="atm-btn-cancelar">Cancelar</button>
                                <button type="submit" className="atm-btn-primary">Registrar Orden</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Ver */}
            {modalVerAbierto && ordenVer && (
                <div className="atm-modal-overlay" onClick={() => setModalVerAbierto(false)}>
                    <div className="atm-modal atm-modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="atm-modal-header">
                            <h3>Orden de Compra #{ordenVer.ordencompra}</h3>
                            <button onClick={() => setModalVerAbierto(false)} className="atm-modal-close">×</button>
                        </div>
                        <div className="atm-modal-body">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '15px' }}>
                                <div><strong>Proveedor:</strong> {ordenVer.idProveedor} - {ordenVer.nomProveedor}</div>
                                <div><strong>Fecha:</strong> {formatearFecha(ordenVer.fecha)}</div>
                                <div><strong>Para Fecha:</strong> {formatearFecha(ordenVer.parafecha)}</div>
                                <div><strong>Forma Pago:</strong> {ordenVer.idFpago || '-'}</div>
                                <div><strong>Condicion:</strong> {ordenVer.condcompra || '-'}</div>
                                <div><strong>Entrega:</strong> {ordenVer.tipoentrega || '-'}</div>
                                <div><strong>Exenta:</strong> {formatearNumero(ordenVer.monExenta)}</div>
                                <div><strong>Total:</strong> {formatearNumero(ordenVer.monTotal)}</div>
                                <div><strong>Gravada 5%:</strong> {formatearNumero(ordenVer.monGravada5)} (IVA {formatearNumero(ordenVer.monIva5)})</div>
                                <div><strong>Gravada 10%:</strong> {formatearNumero(ordenVer.monGravada10)} (IVA {formatearNumero(ordenVer.monIva10)})</div>
                                <div style={{ gridColumn: 'span 2' }}><strong>Observacion:</strong> {ordenVer.observacion || '-'}</div>
                            </div>

                            <h4 style={{ color: '#004F9F' }}>Detalle</h4>
                            <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                <thead>
                                    <tr>
                                        <th>ID</th><th>Producto</th>
                                        <th style={{ textAlign: 'right' }}>Cant.</th>
                                        <th style={{ textAlign: 'right' }}>Precio</th>
                                        <th style={{ textAlign: 'right' }}>Exenta</th>
                                        <th style={{ textAlign: 'right' }}>Grav. 5%</th>
                                        <th style={{ textAlign: 'right' }}>Grav. 10%</th>
                                        <th style={{ textAlign: 'right' }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(ordenVer.detalles || []).map(d => (
                                        <tr key={d.idProducto}>
                                            <td>{d.idProducto}</td>
                                            <td>{d.descripcion}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.cantidad, 2)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.precio, 2)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.monExenta, 2)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.monGravada5, 2)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.monGravada10, 2)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.monTotal, 2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SOrdencompra;
